import { useEffect, useMemo, useRef, useState } from 'react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation } from 'js/lib/useRouter';

import { DEFAULT_ROLE_OPTIONS_LIMIT } from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import useClientSideNumberOfItemsPerRow from 'bundles/onboarding-common/hooks/useClientSideNumberOfItemsPerRow';
import useHandleSelectedItemsChange from 'bundles/onboarding-common/hooks/useHandleSelectedItemsChange';
import useIsAdvancer from 'bundles/onboarding-common/hooks/useIsAdvancer';
import useSearchAndSelectOptionState from 'bundles/onboarding-common/hooks/useSearchAndSelectOptionState';
import type { RoleFragment } from 'bundles/onboarding-common/queries/__generated__/RoleFragment';
import type { Role, RoleWithState, SelectOptionWithState } from 'bundles/onboarding-common/types';

import _t from 'i18n!nls/onboarding-common';

const getInitialState = (defaultValue: RoleWithState[], roles: RoleFragment[]): RoleWithState[] => {
  const allRolesWithState = roles.reduce((acc, role) => {
    const ids = acc.map(({ id }) => id);
    if (!ids.includes(role.id)) {
      const roleWithState: RoleWithState = {
        ...role,
        type: 'DEFAULT',
        isSearchResult: false,
        isSelected: false,
        isVisible: true,
        isPartOfSearch: false,
      };
      return [...acc, roleWithState];
    }
    return acc;
  }, defaultValue);

  return allRolesWithState;
};

const useRoleSelectQuestionState = ({
  defaultValue,
  roles,
}: {
  defaultValue: RoleWithState[];
  roles: RoleFragment[];
}) => {
  const containerRef = useRef(null);
  const location = useLocation();
  const isAdvancer = useIsAdvancer();

  const { onChange, setValidationErrorMessage } = useOnboardingQuestionData();

  const defaultSingleSelectOption: SelectOptionWithState<Role> = {
    id: 'i-dont-know',
    name: _t("I don't know"),
    type: 'UNSURE',
    slug: 'i-dont-know',
    isSelected: false,
  };

  const {
    items: rolesList,
    dispatch,
    singleSelectOption: iDontKnowRole,
  } = useSearchAndSelectOptionState<Role>(getInitialState(defaultValue, roles), defaultSingleSelectOption);

  const referrer = location.search ? `${location.pathname}${location.search}` : location.pathname;
  const { onboardingUrl } = useBuildOnboardingUrl({ questionId: QUESTION_IDS.roleSelect, referrer });

  const [isExpanded, setIsExpanded] = useState(false);

  const rolesLimit = isAdvancer ? 1 : DEFAULT_ROLE_OPTIONS_LIMIT;
  const rolesToDisplay = rolesList.filter(({ isSelected, isVisible }) => isSelected || isVisible);

  const { numberOfItemsPerRow, isHydrated } = useClientSideNumberOfItemsPerRow({
    containerRef,
    items: rolesToDisplay.map(({ name }) => name || ''),
    pinnedItem: iDontKnowRole?.name || '',
    widthData: {
      spacingBetweenItems: 8,
      itemPadding: 16,
      itemBorder: 2,
      buttonWidth: 104,
      iconWidth: 16,
      spacingBetweenIconAndText: 4,
    },
    isExpanded,
  });

  const rolesPerRow = useMemo(() => {
    if (!numberOfItemsPerRow) return null;
    let previousRowLastIndex = 0;
    return numberOfItemsPerRow.map((numberOfItems, index) => {
      const isLastRow = index === numberOfItemsPerRow.length - 1;
      const currentRow = rolesToDisplay.slice(previousRowLastIndex, numberOfItems + previousRowLastIndex);

      if (isLastRow && iDontKnowRole) {
        currentRow.push({ ...iDontKnowRole, isSearchResult: false, isVisible: true, isPartOfSearch: false });
      }

      previousRowLastIndex += numberOfItems;

      return currentRow;
    });
  }, [numberOfItemsPerRow, rolesToDisplay, iDontKnowRole]);

  const handleSelectedRolesChange = (allRoles: SelectOptionWithState<Role>[]) => {
    const selectedRoles = allRoles
      .filter(({ isSelected }) => isSelected)
      .map(({ id, name, type }) => ({ id, name, type }));

    onChange({
      __typename: QUESTION_IDS.roleSelect,
      value: selectedRoles.map((role) => ({
        id: role.id,
        name: role.name || '',
        type: role.type,
      })),
    });
  };

  useHandleSelectedItemsChange([...rolesList, ...(iDontKnowRole ? [iDontKnowRole] : [])], handleSelectedRolesChange);

  useEffect(() => {
    if (rolesToDisplay.filter(({ isSelected }) => isSelected).length > rolesLimit) {
      setValidationErrorMessage(_t("You've reached the limit of #{rolesLimit} selected roles", { rolesLimit }));
    } else {
      setValidationErrorMessage(undefined);
    }
  }, [rolesToDisplay, rolesLimit, setValidationErrorMessage]);

  const handleClick = (role: RoleWithState) => {
    // TODO: Add tracking
    if (role.id === iDontKnowRole?.id) {
      if (iDontKnowRole?.isSelected) {
        dispatch({ type: 'DESELECT_SINGLE_SELECT_OPTION' });
      } else {
        dispatch({ type: 'SELECT_SINGLE_SELECT_OPTION' });
      }
      return;
    }

    if (role.isSelected) {
      dispatch({
        type: 'DESELECT_ITEM_BY_ID',
        payload: role.id,
      });
    } else {
      dispatch({
        type: 'SELECT_ITEM_BY_ID',
        payload: role.id,
      });
    }
  };

  const onViewMoreClick = () => {
    // TODO: Add tracking
    setIsExpanded(true);
  };

  const onViewAllClick = () => {
    // TODO: Add tracking
    setLocation(onboardingUrl);
  };

  const isLoading = !isHydrated;

  return {
    containerRef,
    rolesPerRow,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
    iDontKnowRole,
  };
};

export default useRoleSelectQuestionState;
